import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { abandonBootRestore, restoreSession, type BootOutcome } from '../api/refresh';
import { useSessionState } from './use-session';
import { FullPageLoading } from '../app/layout/full-page-loading';
import { BootUnavailable } from '../app/layout/boot-unavailable';

/**
 * Runs the boot-time session restore, then renders the app
 * (docs/07-implementation-roadmap.md F1.1).
 *
 * The whole tree waits on that one round-trip. The alternative — every screen
 * handling a third "we don't know yet" state — spreads a transient condition
 * across the entire app to save a few hundred milliseconds once per page load,
 * and gets it wrong somewhere. The diagnostics page is deliberately outside this
 * boundary (see `router.tsx`): a page for investigating a broken deployment is
 * no use if it needs the deployment's auth to work.
 *
 * **Boot has three outcomes, not two** (F7.5). A session, no session, or an API
 * that would not answer — the last one being where the office's 429 lands first,
 * since `POST /auth/refresh` is the first request of every page load. It renders
 * as `BootUnavailable` rather than as the login screen, because a login screen
 * would be a false statement about what happened *and* an invitation to spend
 * more of an exhausted request budget.
 *
 * The hooks components use to read the session live in `use-session.ts`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { status } = useSessionState();
  const started = useRef(false);
  const [outcome, setOutcome] = useState<BootOutcome | null>(null);

  const restore = useCallback(() => {
    setOutcome(null);
    void restoreSession().then(setOutcome);
  }, []);

  // Give up and fall through to the login screen. Both halves are needed: the
  // store has to leave `unknown` for the router to render anything, and this
  // component has to stop rendering the outcome that put it here.
  const signIn = useCallback(() => {
    abandonBootRestore();
    setOutcome({ kind: 'anonymous' });
  }, []);

  useEffect(() => {
    // StrictMode mounts effects twice in development. A second restore would be
    // a second refresh, and the API rotates the token on every use — the two
    // would revoke each other, which is precisely the failure F1.3 exists to
    // prevent, reproduced here by the development tooling.
    if (started.current) return;
    started.current = true;
    restore();
  }, [restore]);

  if (outcome?.kind === 'unavailable') {
    return <BootUnavailable error={outcome.error} onRetry={restore} onSignIn={signIn} />;
  }

  if (status === 'unknown') {
    return <FullPageLoading />;
  }

  return <>{children}</>;
}
